import React, { useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/contexts/SidebarContext';
import useSubmenu from '@/hooks/useSubmenu';
import ThemeSwitcher from '@/components/ThemeSwitcher/index';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Submenu from '@/components/ui/Submenu';
import { HeaderSearchBar, HeaderSearchTrigger } from '@/components/HeaderSearch';
import type { HeaderSearchConfig } from '@/contexts/PageConfigContext';
import styles from './styles.module.scss';

interface HeaderProps {
  showBackButton?: boolean;
  onBackClick: () => void;
  solid?: boolean;
  /** Search icon in `.right` (page opt-in). */
  search?: HeaderSearchConfig | null;
  /** Search mode: the header row is only the search bar. */
  searchActive?: boolean;
  hideLanguageSwitcher?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  showBackButton = false,
  onBackClick,
  solid = false,
  search = null,
  searchActive = false,
  hideLanguageSwitcher = false,
}) => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen } = useSidebar();
  const { anchorEl, open: menuOpen, handleClick: handleAvatarClick, handleClose: handleMenuClose } = useSubmenu();
  const headerRef = useRef<HTMLElement>(null);

  // Hide login button on auth pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/sign-up';

  const getAvatarText = () => {
    if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    }
    if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const avatarMenuActions = [
    {
      label: 'Профиль',
      icon: 'user',
      to: '/account/edit',
    },
    {
      label: 'Выход',
      icon: 'exit',
      onClick: handleLogout,
    },
  ];

  return (
    <header className={`${styles.header} ${!isLoading && !isAuthenticated ? styles.transparent : ''} ${solid ? styles.solid : ''}`} data-app-chrome="top" ref={headerRef}>
      {search && searchActive ? (
        <HeaderSearchBar config={search} anchorRef={headerRef} />
      ) : (
        <>
          <div className={styles.left}>
            {showBackButton ? (
              <button
                onClick={onBackClick}
                className={styles.backButton}
                aria-label="Go back"
                data-testid="header-back-button"
              >
                <ArrowBackIosNewIcon />
              </button>
            ) : (
              <button
                className={styles.burger}
                aria-label="Open menu"
                data-testid="header-menu-button"
                onClick={() => setOpen(true)}
              >
                <span className={styles.burgerLine}></span>
                <span className={styles.burgerLine}></span>
                <span className={styles.burgerLine}></span>
              </button>
            )}

          </div>

          <div className={styles.right}>
            {search && <HeaderSearchTrigger config={search} />}
            {!hideLanguageSwitcher && <LanguageSwitcher />}
            <ThemeSwitcher />

            {isAuthenticated && user ? (
              <div className={styles.userBlock}>
                <div className={styles.avatar} onClick={handleAvatarClick}>
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt={user.first_name || user.username || 'User'} />
                  ) : (
                    <div className={styles.avatarFallback}>
                      <span>{getAvatarText()}</span>
                    </div>
                  )}
                </div>
                <div className={styles.userInfo}>
                  <span className={styles.userName}>
                    {user.first_name || user.username || 'User'}
                  </span>
                  <span className={styles.userLocation}>PClash</span>
                </div>
                <Submenu
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorEl={anchorEl}
                  actions={avatarMenuActions}
                />
              </div>
            ) : isLoading && !isAuthPage ? (
              <div className={styles.userBlockPlaceholder} aria-hidden="true">
                <div className={styles.avatarPlaceholder} />
                <div className={styles.userInfoPlaceholder}>
                  <span />
                  <span />
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
